<template>
	<div class="sp-bar">
		<div class="sp-left">
			<label class="sp-label" :for="selectId">Rows per page</label>
			<select :id="selectId" class="sp-select" :value="perPage" @change="setPerPage($event.target.value)">
				<option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
			</select>
			<span class="sp-range">{{ rangeText }}</span>
		</div>

		<nav v-if="pageCount > 1" class="sp-pages" aria-label="Pagination">
			<button type="button" class="sp-btn" :disabled="page <= 1" aria-label="Previous page" @click="go(page - 1)">
				<font-awesome-icon :icon="['fas', 'chevron-left']" />
			</button>
			<template v-for="(p, i) in pages">
				<span v-if="p === '…'" :key="'gap' + i" class="sp-gap">…</span>
				<button
					v-else
					:key="p"
					type="button"
					class="sp-btn"
					:class="{ active: p === page }"
					:aria-current="p === page ? 'page' : null"
					@click="go(p)"
				>
					{{ p }}
				</button>
			</template>
			<button type="button" class="sp-btn" :disabled="page >= pageCount" aria-label="Next page" @click="go(page + 1)">
				<font-awesome-icon :icon="['fas', 'chevron-right']" />
			</button>
		</nav>
	</div>
</template>

<script>
let uid = 0

// Client-side pagination bar: rows-per-page on the left, page numbers on the right.
// Use with .sync: <StatsPagination :total="n" :page.sync="page" :per-page.sync="perPage" />
export default {
	name: 'StatsPagination',
	props: {
		total: { type: Number, required: true },
		page: { type: Number, required: true },
		perPage: { type: Number, required: true },
		perPageOptions: { type: Array, default: () => [10, 25, 50, 100] },
	},
	data() {
		return { selectId: 'sp-per-page-' + ++uid }
	},
	computed: {
		pageCount() {
			return Math.max(1, Math.ceil(this.total / this.perPage))
		},
		rangeText() {
			if (!this.total) return '0 of 0'
			const from = (this.page - 1) * this.perPage + 1
			const to = Math.min(this.total, this.page * this.perPage)
			return `${from}–${to} of ${this.total}`
		},
		// 1 … 4 5 [6] 7 8 … 20 — first, last and two around the current page
		pages() {
			const count = this.pageCount
			if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1)
			const out = [1]
			const start = Math.max(2, this.page - 2)
			const end = Math.min(count - 1, this.page + 2)
			if (start > 2) out.push('…')
			for (let p = start; p <= end; p++) out.push(p)
			if (end < count - 1) out.push('…')
			out.push(count)
			return out
		},
	},
	watch: {
		pageCount(count) {
			if (this.page > count) this.$emit('update:page', count)
		},
	},
	methods: {
		go(p) {
			if (p >= 1 && p <= this.pageCount && p !== this.page) this.$emit('update:page', p)
		},
		setPerPage(value) {
			this.$emit('update:perPage', Number(value))
			this.$emit('update:page', 1)
		},
	},
}
</script>

<style scoped>
.sp-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	padding: 10px 24px;
	border-top: 1px solid #f3f4f6;
	font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace;
	font-size: 12px;
	color: #6b7280;
}

.sp-left {
	display: flex;
	align-items: center;
	gap: 8px;
}

.sp-label {
	margin: 0;
}

.sp-select {
	border: 1px solid #d1d5db;
	border-radius: 6px;
	padding: 2px 6px;
	font: inherit;
	color: #111827;
	background: white;
}

.sp-range {
	margin-left: 8px;
}

.sp-pages {
	display: flex;
	align-items: center;
	gap: 4px;
}

.sp-btn {
	min-width: 28px;
	height: 28px;
	padding: 0 6px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	background: white;
	color: #374151;
	font: inherit;
	cursor: pointer;
	transition: all 0.15s ease;
}

.sp-btn:hover:not(:disabled):not(.active) {
	border-color: #9ca3af;
	color: #111827;
}

.sp-btn.active {
	background: #111827;
	border-color: #111827;
	color: white;
	cursor: default;
}

.sp-btn:disabled {
	opacity: 0.4;
	cursor: default;
}

.sp-gap {
	padding: 0 4px;
}
</style>
